
import React, { useState } from 'react';
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import ArticleCard from "@/components/articles/ArticleCard";
import ArticleFilters from "@/components/articles/ArticleFilters";
import NewsletterSection from "@/components/articles/NewsletterSection";
import { useSupabase } from "@/hooks/useSupabase";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/components/ui/use-toast";
import { mockArticles } from "@/utils/mockArticles";
import type { Article } from "@/types/article";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const Articles: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeTab, setActiveTab] = useState("all");
  const { user } = useSupabase();
  const { hasPremiumAccess } = useSubscription();
  const { toast } = useToast();

  const categories = React.useMemo(() => {
    const set = new Set(["All"]);
    mockArticles.forEach(a => {
      if (a.category) set.add(a.category);
    });
    return Array.from(set);
  }, []);

  const handlePremiumAccess = (isPremium: boolean) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to access premium content",
        variant: "destructive"
      });
      return false;
    }
    
    if (isPremium && !hasPremiumAccess) {
      toast({
        title: "Premium Content",
        description: "Upgrade to access premium articles",
        action: (
          <Link to="/pricing">
            <Button variant="outline" size="sm">
              View Plans
            </Button>
          </Link>
        )
      });
      return false;
    }
    
    return true;
  };

  const filteredArticles = mockArticles.filter(article => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "premium" && article.premium) ||
      (activeTab === "free" && !article.premium);
    return matchesSearch && matchesCategory && matchesTab;
  });

  return (
    <Layout>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 text-center">AI Nexus Feed</h1>
          <p className="mt-4 text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Explore the latest insights, research, and trends in artificial intelligence
          </p>
          <ArticleFilters
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            activeTab={activeTab}
            categories={categories}
            onSearchChange={setSearchTerm}
            onCategoryChange={setSelectedCategory}
            onTabChange={setActiveTab}
          />
        </div>
      </div>
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-lg font-medium text-gray-900">No articles found</h3>
              <p className="mt-2 text-gray-500">Try adjusting your search or filter criteria</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory("All");
                  setActiveTab("all");
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3"
                 style={{ justifyItems: "center" }}>
              {filteredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  hasPremiumAccess={hasPremiumAccess}
                  onPremiumAccess={handlePremiumAccess}
                />
              ))}
            </div>
          )}
          
          <div className="mt-12">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive>1</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">2</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#">3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext href="#" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </section>
      <NewsletterSection />
    </Layout>
  );
};

export default Articles;

